import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Check, ChevronLeft, ChevronRight, Copy, Download } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getFoodMenu } from "@/lib/food-menu.functions";
import type { Locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";


type FoodMenuItem = {
  date: string;
  day_name: string;
  theme_day_title: string | null;
  main_dish: string | null;
  vegetarian: string | null;
  side_dish: string | null;
  allergens: string[];
  allergens_main: string[];
  allergens_veg: string[];
  allergens_side: string[];
  theme_day_image_url: string | null;
};

type ViewMode = "week" | "month" | "list";

const VIEW_STORAGE_KEY = "food-menu-view";

const MONTHS: Record<Locale, string[]> = {
  sv: [
    "januari",
    "februari",
    "mars",
    "april",
    "maj",
    "juni",
    "juli",
    "augusti",
    "september",
    "oktober",
    "november",
    "december",
  ],
  en: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  es: [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
  ],
};

const WEEKDAYS: Record<Locale, string[]> = {
  sv: ["MÅN", "TIS", "ONS", "TOR", "FRE"],
  en: ["MON", "TUE", "WED", "THU", "FRI"],
  es: ["LUN", "MAR", "MIÉ", "JUE", "VIE"],
};

const T: Record<
  Locale,
  {
    loading: string;
    empty: string;
    main: string;
    todayHeading: string;
    veg: string;
    side: string;
    allergens: string;
    pdf: string;
    today: string;
    todayNone: string;
    week: string;
    month: string;
    list: string;
    weekShort: string;
    copy: string;
    copied: string;
    lastDay: string;
    noService: string;
    prev: string;
    next: string;
    servingDays: string;
    weeksLabel: string;
    allergenNote: string;
    ourPdf: string;
    originalPdf: string;
    menuLanguage: string;
  }
> = {
  sv: {
    loading: "Hämtar matsedeln …",
    empty:
      "Ingen matsedel är publicerad för den här månaden. Under lov publiceras ingen matsedel.",
    main: "Huvudrätt",
    todayHeading: "Dagens lunch",
    veg: "Vegetariskt",
    side: "Tillbehör",
    allergens: "Allergener",
    pdf: "Ladda ner som PDF",
    today: "Idag",
    todayNone: "Ingen servering idag.",
    week: "Vecka",
    month: "Månad",
    list: "Lista",
    weekShort: "v.",
    copy: "Kopiera dagens meny",
    copied: "Kopierat!",
    lastDay: "SISTA DAGEN",
    noService: "Ingen servering",
    prev: "Föregående månad",
    next: "Nästa månad",
    servingDays: "serveringsdagar",
    weeksLabel: "veckor",
    allergenNote:
      "Klicka på en allergen för förklaring. Med reservation för ändringar – kontakta skolan vid allergier eller specialkost.",
    ourPdf: "Vår PDF",
    originalPdf: "Originalet",
    menuLanguage: "Matsedelns språk",
  },
  en: {
    loading: "Loading the menu …",
    empty:
      "No menu has been published for this month. No menu is published during school holidays.",
    main: "Main course",
    todayHeading: "Today's lunch",
    veg: "Vegetarian",
    side: "Sides",
    allergens: "Allergens",
    pdf: "Download as PDF",
    today: "Today",
    todayNone: "No lunch served today.",
    week: "Week",
    month: "Month",
    list: "List",
    weekShort: "w.",
    copy: "Copy this day's menu",
    copied: "Copied!",
    lastDay: "LAST DAY",
    noService: "No service",
    prev: "Previous month",
    next: "Next month",
    servingDays: "serving days",
    weeksLabel: "weeks",
    allergenNote:
      "Tap an allergen for details. Menus may change – contact the school about allergies or special diets.",
    ourPdf: "Our PDF",
    originalPdf: "Original",
    menuLanguage: "Menu language",
  },
  es: {
    loading: "Cargando el menú…",
    empty: "No hay menú publicado para este mes. Durante las vacaciones escolares no se publica menú.",
    main: "Plato principal",
    todayHeading: "Comida de hoy",
    veg: "Vegetariano",
    side: "Guarnición",
    allergens: "Alérgenos",
    pdf: "Descargar en PDF",
    today: "Hoy",
    todayNone: "Hoy no hay servicio de comedor.",
    week: "Semana",
    month: "Mes",
    list: "Lista",
    weekShort: "sem.",
    copy: "Copiar el menú de hoy",
    copied: "¡Copiado!",
    lastDay: "ÚLTIMO DÍA",
    noService: "Sin servicio",
    prev: "Mes anterior",
    next: "Mes siguiente",
    servingDays: "días de servicio",
    weeksLabel: "semanas",
    allergenNote: "Pulsa un alérgeno para ver los detalles. El menú puede cambiar; contacta con el colegio para alergias o dietas especiales.",
    ourPdf: "Nuestro PDF",
    originalPdf: "Original",
    menuLanguage: "Idioma del menú",
  },
};

function parseDate(date: string) {
  return new Date(`${date}T12:00:00Z`);
}

function isoWeek(date: Date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function todayIso() {
  // Gör det möjligt att förhandsgranska en annan dag: ?dag=2026-06-03
  if (typeof window !== "undefined") {
    const param = new URLSearchParams(window.location.search).get("dag");
    if (param && /^\d{4}-\d{2}-\d{2}$/.test(param)) return param;
  }
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;
}

function formatDay(date: string, locale: Locale) {
  return new Intl.DateTimeFormat(locale === "sv" ? "sv-SE" : locale === "es" ? "es-ES" : "en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(parseDate(date));
}

/** Kort förklaring till de allergener som förekommer i matsedeln. */
const ALLERGEN_INFO: Record<string, { sv: string; en: string }> = {
  gluten: { sv: "Innehåller gluten (vete, råg, korn eller havre).", en: "Contains gluten (wheat, rye, barley or oats)." },
  laktos: { sv: "Innehåller laktos (mjölksocker).", en: "Contains lactose (milk sugar)." },
  mjolk: { sv: "Innehåller mjölkprotein.", en: "Contains milk protein." },
  agg: { sv: "Innehåller ägg.", en: "Contains egg." },
  fisk: { sv: "Innehåller fisk.", en: "Contains fish." },
  skaldjur: { sv: "Innehåller skaldjur.", en: "Contains crustaceans." },
  blotdjur: { sv: "Innehåller blötdjur, till exempel musslor eller bläckfisk.", en: "Contains molluscs, for example mussels or squid." },
  notter: { sv: "Innehåller nötter.", en: "Contains tree nuts." },
  jordnotter: { sv: "Innehåller jordnötter.", en: "Contains peanuts." },
  soja: { sv: "Innehåller soja.", en: "Contains soy." },
  selleri: { sv: "Innehåller selleri.", en: "Contains celery." },
  senap: { sv: "Innehåller senap.", en: "Contains mustard." },
  sesam: { sv: "Innehåller sesamfrön.", en: "Contains sesame seeds." },
  sulfit: { sv: "Innehåller sulfit.", en: "Contains sulphites." },
  lupin: { sv: "Innehåller lupin.", en: "Contains lupin." },
};

/** Enbokstavskoder som skolans matsedel använder. */
const ALLERGEN_CODES: Record<string, keyof typeof ALLERGEN_INFO> = {
  G: "gluten",
  L: "mjolk",
  E: "agg",
  F: "fisk",
  C: "selleri",
  S: "soja",
  M: "senap",
  N: "notter",
  P: "jordnotter",
  O: "blotdjur",
  "★": "sulfit",
  "◆": "skaldjur",
  "■": "lupin",
  "§": "sesam",
};

/** Kort namn på allergenet, för synliga etiketter. */
const ALLERGEN_LABEL: Record<string, { sv: string; en: string }> = {
  gluten: { sv: "Gluten", en: "Gluten" },
  laktos: { sv: "Laktos", en: "Lactose" },
  mjolk: { sv: "Mjölk", en: "Milk" },
  agg: { sv: "Ägg", en: "Egg" },
  fisk: { sv: "Fisk", en: "Fish" },
  skaldjur: { sv: "Skaldjur", en: "Crustaceans" },
  blotdjur: { sv: "Blötdjur", en: "Molluscs" },
  notter: { sv: "Nötter", en: "Tree nuts" },
  jordnotter: { sv: "Jordnötter", en: "Peanuts" },
  soja: { sv: "Soja", en: "Soy" },
  selleri: { sv: "Selleri", en: "Celery" },
  senap: { sv: "Senap", en: "Mustard" },
  sesam: { sv: "Sesam", en: "Sesame" },
  sulfit: { sv: "Sulfit", en: "Sulphites" },
  lupin: { sv: "Lupin", en: "Lupin" },
};

function allergenKey(name: string) {
  const raw = name.trim();
  const code = ALLERGEN_CODES[raw.toUpperCase()] ?? ALLERGEN_CODES[raw];
  if (code) return code;
  return raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z]/g, "");
}

function allergenLabel(name: string, locale: Locale) {
  const info = ALLERGEN_LABEL[allergenKey(name)];
  return info ? info[locale === "sv" ? "sv" : "en"] : name.trim();
}

function allergenText(name: string, locale: Locale) {
  const info = ALLERGEN_INFO[allergenKey(name)];
  if (info) return info[locale === "sv" ? "sv" : "en"];
  return locale === "sv"
    ? `Rätten innehåller ${name.toLowerCase()}.`
    : locale === "es" ? `El plato contiene ${name.toLowerCase()}.` : `This dish contains ${name.toLowerCase()}.`;
}


function AllergenChip({ name, locale }: { name: string; locale: Locale }) {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="rounded-full border border-primary/15 bg-primary/[0.06] px-2 py-[2px] text-[0.65rem] leading-[1.3] font-medium tracking-wide text-primary/90 transition-colors hover:border-primary/40 hover:bg-primary/12 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
        >
          {allergenLabel(name, locale)}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-56 text-xs">
        {allergenText(name, locale)}
      </TooltipContent>
    </Tooltip>
  );
}

function Allergens({ list, locale }: { list: string[]; locale: Locale }) {
  if (!list || list.length === 0) return null;
  return (
    <span className="mt-1.5 flex flex-wrap gap-1">
      {list.map((a) => (
        <AllergenChip key={a} name={a} locale={locale} />
      ))}
    </span>
  );
}


function DishBlock({
  label,
  text,
  allergens,
  strong,
  flat,
  locale,
}: {
  label: string;
  text: string | null;
  allergens: string[];
  strong?: boolean;
  flat?: boolean;
  locale: Locale;
}) {
  if (!text) return null;
  return (
    <div
      className={cn(
        flat
          ? ""
          : "mt-3 border-t border-border/50 pt-3 first:mt-0 first:border-0 first:pt-0",
      )}
    >
      <p className="text-[0.58rem] font-semibold tracking-[0.16em] text-primary/60 uppercase">
        {label}
      </p>
      <p
        className={cn(
          "mt-1 text-[0.9rem] leading-snug text-pretty",
          strong ? "font-semibold text-foreground" : "text-foreground/70",
        )}
      >
        {text}
      </p>
      <Allergens list={allergens} locale={locale} />
    </div>
  );
}


/** Bygger en läsbar textrad av dagens meny, för urklipp. */
function dayAsText(item: FoodMenuItem, locale: Locale, t: (typeof T)[Locale]) {
  const lines: string[] = [formatDay(item.date, locale)];
  if (item.theme_day_title) lines.push(item.theme_day_title);
  const block = (label: string, text: string | null, allergens: string[]) => {
    if (!text) return;
    const a = allergens.map((x) => allergenLabel(x, locale)).join(", ");
    lines.push(`${label}: ${text}${a ? ` (${a})` : ""}`);
  };
  block(t.main, item.main_dish, item.allergens_main);
  block(t.veg, item.vegetarian, item.allergens_veg);
  block(t.side, item.side_dish, item.allergens_side);
  return lines.join("\n");
}

function CopyDayButton({
  item,
  locale,
  t,
}: {
  item: FoodMenuItem;
  locale: Locale;
  t: (typeof T)[Locale];
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    const text = dayAsText(item, locale, t);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      el.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={copy}
          aria-label={t.copy}
          className={cn(
            "absolute top-2 right-2 grid size-7 place-items-center rounded-full border border-border/70 bg-background/90 text-primary shadow-sm transition-all",
            "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none",
            copied && "opacity-100 border-primary/45 bg-primary/10",
          )}
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {copied ? t.copied : t.copy}
      </TooltipContent>
    </Tooltip>
  );
}

function DayCard({
  item,
  locale,
  t,
  header,
  today,
}: {
  item: FoodMenuItem;
  locale: Locale;
  t: (typeof T)[Locale];
  header: React.ReactNode;
  today?: boolean;
}) {
  return (
    <div
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-xl bg-background transition-all duration-200 hover:-translate-y-0.5 hover:shadow-soft",
        today
          ? "border-2 border-primary shadow-soft ring-2 ring-primary/15"
          : "border border-border/60 hover:border-primary/30",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "absolute inset-x-0 top-0 transition-colors",
          today ? "h-[5px] bg-primary" : "h-[3px] bg-transparent group-hover:bg-primary/25",
        )}
      />

      <CopyDayButton item={item} locale={locale} t={t} />
      <div className="border-b border-border/50 bg-sand/35 px-4 pt-3.5 pb-2.5 pr-9">{header}</div>
      <div className="flex flex-1 flex-col px-4 pt-3 pb-4">
      {item.theme_day_title ? (
        <p className="mb-3 inline-flex w-fit rounded-full bg-primary/8 px-2.5 py-0.5 text-[0.7rem] font-medium tracking-wide text-primary">
          {item.theme_day_title}
        </p>
      ) : null}
      <div>

        <DishBlock
          label={t.main}
          text={item.main_dish}
          allergens={item.allergens_main}
          strong
          locale={locale}
        />
        <DishBlock
          label={t.veg}
          text={item.vegetarian}
          allergens={item.allergens_veg}
          locale={locale}
        />
        <DishBlock
          label={t.side}
          text={item.side_dish}
          allergens={item.allergens_side}
          locale={locale}
        />
      </div>
      </div>
    </div>

  );
}



export function FoodMenu({ locale }: { locale: Locale }) {
  const t = T[locale];
  const months = MONTHS[locale];
  const fetchMenu = useServerFn(getFoodMenu);
  const [selected, setSelected] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>("week");
  // Matsedeln finns på svenska, engelska och spanska – besökaren kan växla fritt.
  const [menuLang, setMenuLang] = useState<"sv" | "en" | "es">(locale);
  // Sätts efter hydrering så att en ev. ?dag=-simulering inte krockar med SSR.
  const [iso, setIso] = useState<string | null>(null);

  useEffect(() => {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === "week" || stored === "month" || stored === "list") setView(stored);
    setIso(todayIso());
  }, []);

  function changeView(next: ViewMode) {
    setView(next);
    window.localStorage.setItem(VIEW_STORAGE_KEY, next);
  }

  const [year, month] = selected ? selected.split("-").map(Number) : [undefined, undefined];

  const { data, isPending } = useQuery({
    queryKey: ["food-menu", menuLang, selected],
    queryFn: () =>
      fetchMenu({
        data: { lang: menuLang, ...(year && month ? { year, month } : {}) },
      }),
    // Visa cachad meny direkt, hämta ev. ändringar i bakgrunden.
    staleTime: 30 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const todayYear = iso ? Number(iso.slice(0, 4)) : undefined;
  const todayMonth = iso ? Number(iso.slice(5, 7)) : undefined;

  const todayQuery = useQuery({
    queryKey: ["food-menu-today", menuLang, iso?.slice(0, 7) ?? null],
    queryFn: () =>
      fetchMenu({
        data: {
          lang: menuLang,
          ...(todayYear && todayMonth ? { year: todayYear, month: todayMonth } : {}),
        },
      }),
    staleTime: 30 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });


  const detail = data?.detail ?? null;
  const current = detail?.menu ?? null;
  const items = detail?.items ?? [];
  // Skolsystemets egen PDF finns bara för vissa månader.
  const hasOriginalPdf = Boolean(
    current &&
      (data?.menus ?? []).some(
        (m) => m.year === current.year && m.month === current.month && m.pdf_url,
      ),
  );

  const todayItem = iso
    ? (items.find((i) => i.date === iso) ??
      todayQuery.data?.detail?.items.find((i) => i.date === iso) ??
      null)
    : null;

  // Navigera alltid exakt en kalendermånad, även om månaden saknar matsedel.
  const displayedMonth = selected
    ? { year: Number(selected.split("-")[0]), month: Number(selected.split("-")[1]) }
    : current;
  const adjacentMonth = (offset: number) => {
    if (!displayedMonth) return undefined;
    const date = new Date(Date.UTC(displayedMonth.year, displayedMonth.month - 1 + offset, 1));
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1 };
  };
  const prev = adjacentMonth(-1);
  const next = adjacentMonth(1);
  const onNowMenu =
    !!current && current.year === todayYear && current.month === todayMonth;
  const nowMenu =
    todayYear && todayMonth ? { year: todayYear, month: todayMonth } : undefined;


  const weeks = useMemo(() => {
    const map = new Map<number, FoodMenuItem[]>();
    for (const item of items) {
      const w = isoWeek(parseDate(item.date));
      const list = map.get(w) ?? [];
      list.push(item);
      map.set(w, list);
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [items]);

  const monthAllergens = useMemo(() => {
    const set = new Set<string>();
    for (const item of items) {
      for (const a of [
        ...item.allergens_main,
        ...item.allergens_veg,
        ...item.allergens_side,
      ]) {
        if (a && a.trim()) set.add(a.trim());
      }
    }
    return [...set].sort((a, b) =>
      allergenLabel(a, locale).localeCompare(allergenLabel(b, locale), locale),
    );
  }, [items, locale]);

  const monthGrid = useMemo(() => {
    if (weeks.length === 0) return [];
    return weeks.map(([week, list]) => {
      const slots: (FoodMenuItem | null)[] = [null, null, null, null, null];
      for (const item of list) {
        const day = (parseDate(item.date).getUTCDay() || 7) - 1;
        if (day >= 0 && day < 5) slots[day] = item;
      }
      return { week, slots };
    });
  }, [weeks]);

  const titleLabel = displayedMonth
    ? `${months[displayedMonth.month - 1]} ${displayedMonth.year}`
    : "";

  return (
    <TooltipProvider delayDuration={120}>
      <section className="mx-auto max-w-6xl px-5 pt-8 pb-6 sm:pt-12 sm:pb-10">
        <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
          {/* Verktygsrad */}
          <div className="flex flex-col gap-4 border-b border-border/60 bg-sand/40 px-4 py-4 sm:px-8 sm:py-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex shrink-0 items-center rounded-full border border-border bg-background p-0.5">
                <button
                  type="button"
                  aria-label={t.prev}
                  disabled={!prev}
                  onClick={() => prev && setSelected(`${prev.year}-${prev.month}`)}
                  className="grid size-8 place-items-center rounded-full text-primary transition-colors hover:bg-sand disabled:opacity-30"
                >
                  <ChevronLeft className="size-4" />
                </button>
                <button
                  type="button"
                  aria-label={t.next}
                  disabled={!next}
                  onClick={() => next && setSelected(`${next.year}-${next.month}`)}
                  className="grid size-8 place-items-center rounded-full text-primary transition-colors hover:bg-sand disabled:opacity-30"
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-xl leading-tight font-semibold capitalize sm:text-2xl">
                  {titleLabel}
                </h2>
                {items.length > 0 ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {items.length} {t.servingDays} · {weeks.length} {t.weeksLabel}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={!nowMenu || onNowMenu}
                onClick={() => nowMenu && setSelected(`${nowMenu.year}-${nowMenu.month}`)}
                className={cn(
                  "h-10 shrink-0 rounded-full border px-4 text-sm font-medium transition-colors",
                  onNowMenu || !nowMenu
                    ? "border-primary/20 bg-primary/8 text-primary/60"
                    : "border-border bg-background text-primary hover:bg-sand",
                )}
              >
                {t.today}
              </button>
              <div
                role="tablist"
                aria-label={t.list}
                className="flex shrink-0 gap-1 rounded-full border border-border bg-background p-1"
              >

                {([
                  ["week", t.week],
                  ["month", t.month],
                  ["list", t.list],
                ] as const).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={view === key}
                    onClick={() => changeView(key)}
                    className={cn(
                      "rounded-full px-3 py-1.5 text-sm font-medium transition-colors sm:px-4",
                      view === key
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-foreground/70 hover:bg-sand",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div
                role="group"
                aria-label={t.menuLanguage}
                className="flex shrink-0 gap-1 rounded-full border border-border bg-background p-1"
              >
                {([
                  ["sv", "SV"],
                  ["en", "EN"],
                  ["es", "ES"],
                ] as const).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={menuLang === key}
                    onClick={() => setMenuLang(key)}
                    className={cn(
                      "rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                      menuLang === key
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-foreground/70 hover:bg-sand",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {current && items.length > 0 ? (
                <a
                  className="inline-flex h-10 items-center gap-2 rounded-full border border-primary/25 bg-background px-4 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                  href={`/api/public/matsedel-pdf?year=${current.year}&month=${current.month}&lang=${menuLang}&layout=5`}
                  download
                  rel="noopener"
                  title={t.pdf}
                >
                  <Download className="size-4" />
                  <span className="hidden sm:inline">{t.ourPdf}</span>
                  <span className="sm:hidden">PDF</span>
                </a>
              ) : null}
              {current && hasOriginalPdf ? (
                <a
                  className="inline-flex h-10 items-center gap-2 rounded-full border border-border bg-background px-4 text-sm font-medium text-foreground/80 transition-colors hover:bg-sand"
                  href={`/api/public/matsedel-original?year=${current.year}&month=${current.month}`}
                  download
                  rel="noopener"
                >
                  <Download className="size-4" />
                  <span>{t.originalPdf}</span>
                </a>
              ) : null}
            </div>
          </div>

          <div className="px-4 py-6 sm:px-8 sm:py-8">
          {/* Servering idag */}
          {todayItem ? (
            <div className="mb-8 overflow-hidden rounded-xl border border-primary/25 bg-primary/[0.04]">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-primary/15 bg-primary/[0.06] px-4 py-2.5 sm:px-5">
                <p className="text-[0.65rem] font-semibold tracking-[0.18em] text-primary uppercase">
                  {t.todayHeading}
                </p>
                <p className="text-sm font-medium capitalize">
                  {formatDay(todayItem.date, locale)}
                </p>
                <span className="text-xs text-muted-foreground">
                  {t.weekShort} {isoWeek(parseDate(todayItem.date))}
                </span>
              </div>
              <div className="grid gap-x-8 gap-y-4 px-4 py-4 sm:grid-cols-3 sm:px-5 sm:py-5">
                <DishBlock
                  label={t.main}
                  text={todayItem.main_dish}
                  flat
                  allergens={todayItem.allergens_main}
                  strong
                  locale={locale}
                />
                <DishBlock
                  label={t.veg}
                  text={todayItem.vegetarian}
                  flat
                  allergens={todayItem.allergens_veg}
                  locale={locale}
                />
                <DishBlock
                  label={t.side}
                  text={todayItem.side_dish}
                  flat
                  allergens={todayItem.allergens_side}
                  locale={locale}
                />
              </div>
            </div>
          ) : (
            <div className="mb-8 flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-xl border border-border/60 bg-sand/30 px-4 py-3 sm:px-5">
              <p className="text-[0.65rem] font-semibold tracking-[0.18em] text-primary/70 uppercase">
                {t.todayHeading}
              </p>
              <p className="text-sm text-muted-foreground">{t.todayNone}</p>
            </div>
          )}



          {isPending ? (
            <p className="text-sm text-muted-foreground">{t.loading}</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t.empty}</p>
          ) : view === "week" ? (
            <div className="space-y-10">
              {weeks.map(([week, list]) => (
                <div key={week}>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold tracking-wide text-primary">
                      {t.weekShort} {week}
                    </span>
                    <span className="text-sm font-medium text-foreground/80">
                      {parseDate(list[0]!.date).getUTCDate()}–
                      {parseDate(list[list.length - 1]!.date).getUTCDate()}{" "}
                      <span className="capitalize">{months[current!.month - 1]}</span>
                    </span>
                    <span className="h-px flex-1 bg-border/70" />
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                    {(() => {
                      const slots: (FoodMenuItem | null)[] = [null, null, null, null, null];
                      for (const item of list) {
                        const day = (parseDate(item.date).getUTCDay() || 7) - 1;
                        if (day >= 0 && day < 5) slots[day] = item;
                      }
                      const renderCard = (item: FoodMenuItem) => (
                        <DayCard
                          key={item.date}
                          item={item}
                          locale={locale}
                          t={t}
                          today={item.date === iso}
                          header={
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="text-xs font-semibold tracking-wide text-primary uppercase">
                                {WEEKDAYS[locale][(parseDate(item.date).getUTCDay() || 7) - 1]}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {parseDate(item.date).getUTCDate()}{" "}
                                {months[current!.month - 1]!.slice(0, 3)}
                              </span>
                            </div>
                          }
                        />
                      );
                      return (
                        <>
                          {/* Mobil/platta: bara dagar som finns, i ordning */}
                          <div className="contents lg:hidden">
                            {list.map(renderCard)}
                          </div>
                          {/* Dator: dagarna hamnar i rätt veckodagskolumn */}
                          <div className="hidden lg:contents">
                            {slots.map((item, i) =>
                              item ? (
                                renderCard(item)
                              ) : (
                                <div
                                  key={`${week}-empty-${i}`}
                                  aria-hidden="true"
                                />
                              ),
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          ) : view === "month" ? (
            <div className="space-y-4">
              <div className="hidden grid-cols-[3rem_repeat(5,minmax(0,1fr))] gap-3 lg:grid">
                <span />
                {WEEKDAYS[locale].map((d) => (
                  <p
                    key={d}
                    className="text-center text-[0.65rem] font-semibold tracking-[0.16em] text-primary/70 uppercase"
                  >
                    {d}
                  </p>
                ))}
              </div>
              {monthGrid.map(({ week, slots }) => (
                <div
                  key={week}
                  className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[3rem_repeat(5,minmax(0,1fr))]"
                >
                  <div className="flex items-start sm:col-span-2 lg:col-span-1">
                    <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                      {t.weekShort} {week}
                    </span>
                  </div>
                  {slots.map((item, i) =>
                    item ? (
                      <DayCard
                        key={item.date}
                        item={item}
                        locale={locale}
                        t={t}
                        today={item.date === iso}
                        header={
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="text-xs font-semibold tracking-wide text-primary uppercase">
                              {WEEKDAYS[locale][(parseDate(item.date).getUTCDay() || 7) - 1]}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {parseDate(item.date).getUTCDate()}
                            </span>
                          </div>
                        }
                      />
                    ) : (
                      <div
                        key={`${week}-${i}`}
                        aria-hidden="true"
                        className="hidden lg:block"
                      />
                    ),
                  )}
                </div>
              ))}
            </div>
          ) : (
            <ul className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60">
              {items.map((item) => (
                <li
                  key={item.date}
                  className={cn(
                    "relative grid gap-x-8 gap-y-2 py-4 pr-4 pl-4 transition-colors sm:grid-cols-[11rem_minmax(0,1fr)] sm:pr-5",
                    item.date === iso
                      ? "border-l-[3px] border-l-primary bg-primary/[0.05] pl-[calc(1rem-3px)]"
                      : "bg-background hover:bg-sand/30",
                  )}
                >
                  <div>

                    <p className="text-sm font-semibold capitalize">{formatDay(item.date, locale)}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t.weekShort} {isoWeek(parseDate(item.date))}
                      {item.theme_day_title ? ` · ${item.theme_day_title}` : ""}
                    </p>
                  </div>
                  <div className="grid gap-x-8 gap-y-3 sm:grid-cols-3">
                    <DishBlock
                      label={t.main}
                      text={item.main_dish}
                      flat
                      allergens={item.allergens_main}
                      strong
                      locale={locale}
                    />
                    <DishBlock
                      label={t.veg}
                      text={item.vegetarian}
                      flat
                      allergens={item.allergens_veg}
                      locale={locale}
                    />
                    <DishBlock
                      label={t.side}
                      text={item.side_dish}
                      flat
                      allergens={item.allergens_side}
                      locale={locale}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}

          {monthAllergens.length > 0 ? (
            <div className="mt-8 rounded-xl border border-border/60 bg-sand/30 px-4 pt-4 pb-5 sm:px-5">
              <p className="text-[0.65rem] font-semibold tracking-[0.18em] text-primary/70 uppercase">
                {t.allergens}
              </p>
              <div className="mt-3 flex flex-wrap gap-x-2 gap-y-2.5">
                {monthAllergens.map((a) => (
                  <AllergenChip key={a} name={a} locale={locale} />
                ))}
              </div>
              <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{t.allergenNote}</p>

            </div>
          ) : null}
          </div>
        </div>
      </section>
    </TooltipProvider>
  );
}


